import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import miniLogo from "../mini-logo.svg";
import { FaChevronRight } from "react-icons/fa";
import { initializeFormData } from "../../../account/fid/add-card/utils/formInitializers";
import { ADD_FID_CARD, CREATE_FID_CARD_CASHBACK } from "../../../../constants/routes";
import { COMPANY_IDS } from "../../../account/fid/fidelityForms/constants/companyName";
import { notifyError } from "../../../../utils/Notify";
import clsx from "clsx";
import hundred from "../hundred.svg";
import useAxios from "../../../../axios/useAxios";
import { useDispatch } from "react-redux";
import { createFidelityCard } from "../../../../services/fidelityService";
import { getJustPhoneNumber } from "../../../account/fid/add-card/utils/validation";
import OptinStep from "../../../account/fid/add-card/steps/OptinStep";
import { posthogUserCaptureEvent } from '../../../../posthog/userEvent';
import {
    POSTHOG_CAPTURE_CREATE_FID_CARD, POSTHOG_CAPTURE_CREATE_FID_CARD_FAIL, POSTHOG_CAPTURE_CREATE_FID_CARD_SUCCESS,
} from '../../../../posthog/constants';
import { getUserDisplayName, getUserDataWithFidelityPriority, calculateFormProgress } from "../../../account/fid/fidelityForms/utils/formHelpers";
import { isEmailValid, isValidBirthDate, isValidPhoneNumber, isValidGender } from "../../../../utils/Validator";

export const CreateFidCardBottomLayer = ({ user, isLoading: externalLoading, favoriteStoreIsMatch, storeId, isActiveStoreValid, onCardCreated, onStartCreation, onError, store }) => {
    const [formData, setFormData] = useState(initializeFormData(user));  
    const navigate = useNavigate();
    const axiosInstance = useAxios();
    const dispatch = useDispatch();
    const [isCreating, setIsCreating] = useState(false);
    const [emailOptin, setEmailOptin] = useState(false);
    const [smsOptin, setSmsOptin] = useState(false);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [progressPercentage, setProgressPercentage] = useState(0);
    const [userGender, setUserGender] = useState('');

    const missingFieldsCount = [
        !formData.fullName,
        !formData.civility,
        !formData.birthDate?.day || !formData.birthDate?.month || !formData.birthDate?.year,
        !formData.email,
        !formData.phone,
        !formData.store,
        userGender && !isValidGender(userGender)
    ].filter(Boolean).length;

    const handleGoToUpdateUser = () => {
        let errorMessage = "Le magasin sélectionné ne prend pas en charge le programme de fidélité avec Pikkopay";
        if (!isActiveStoreValid) {
            notifyError(errorMessage);
            return;
        }
        let companyNameStandard = companyInfo?.name?.toLowerCase();
        if(companyNameStandard === "match"){
            navigate(`/fidelity-form/${COMPANY_IDS.MATCH}`, {
                state: {
                    storeId,
                    previousRoute: CREATE_FID_CARD_CASHBACK,
                    companyInfo
                }
            });
        }else{
            notifyError(errorMessage);
        }
    };

      const goToAddCard = () => {
            navigate(ADD_FID_CARD, {
            state: {
                storeId,
                previousRoute: CREATE_FID_CARD_CASHBACK,
                companyId: companyInfo?.id,
                companyLogo: companyInfo?.img,
                choice: 'add',
            },
            });
        };

    const handleCreateCard = async () => {
        if (missingFieldsCount > 0 || !isActiveStoreValid) {
            notifyError("Complète toutes les informations requises");
            return;
        }
        posthogUserCaptureEvent(user, POSTHOG_CAPTURE_CREATE_FID_CARD)


        setIsCreating(true);
        if (onStartCreation) {
            onStartCreation();
        }

        try {
            let dialCode = '33';
            let phoneForExtraction = formData.phone;

            if (formData.phone && formData.phone.startsWith('+')) {
                const match = formData.phone.match(/^\+(\d+)/);
                if (match) {
                    dialCode = match[1].substring(0, 2);
                    phoneForExtraction = formData.phone;
                }
            } else if (formData.phone && !formData.phone.startsWith('+')) {
                phoneForExtraction = formData.phone;
            }

            const justPhoneNumber = getJustPhoneNumber(phoneForExtraction, dialCode);

            const submissionData = {
                fullName: formData.fullName,
                civility: formData.civility,
                birthDate: formData.birthDate,
                email: formData.email,
                phone: formData.phone,
                justPhoneNumber,
                indicatif: dialCode,
                emailOptin,
                smsOptin,
                store: formData.store || user?.favoriteStore
            };

            const result = await createFidelityCard(
                submissionData,
                axiosInstance,
                user?._id,
                formData.store || user?.favoriteStore,
                dispatch
            );
            if (result.data?.success) {
                try {
                    const companyCardsResponse = await axiosInstance.get(`/user/${user._id}/fidelity-cards`);
                    const cards = companyCardsResponse.data;
                    const createdId = result.data?.data?.userFidCard?.[0]?._id;
                    const companyCard = cards.find(c => c._id === createdId) || cards.find(c => c.companyId === companyInfo?.id);
                    const fullCard = result.data?.data?.userFidCard?.[0];

                    if (fullCard && onCardCreated) {
                        posthogUserCaptureEvent(user, POSTHOG_CAPTURE_CREATE_FID_CARD_SUCCESS)
                        onCardCreated({
                            cardImage: companyCard?.cardImage,
                            companyLogo: companyCard?.companyImage,
                            name: fullCard?.fidelityInfo?.ficheClient?.nom,
                            surname: fullCard?.fidelityInfo?.ficheClient?.prenom,
                            numero: fullCard?.fidelityInfo?.ficheClient?.numeroCarte
                        });
                    }
                } catch (fetchError) {
                    console.error("Error fetching updated cards:", fetchError);
                    posthogUserCaptureEvent(user, POSTHOG_CAPTURE_CREATE_FID_CARD_FAIL)
                    const createdCardData = result.data?.data?.userFidCard?.[0];
                    if (createdCardData && onCardCreated) {
                        onCardCreated({
                            cardImage: createdCardData.cardImage,
                            companyLogo: createdCardData.companyImage,
                            name: createdCardData?.fidelityInfo?.ficheClient?.nom,
                            surname: createdCardData?.fidelityInfo?.ficheClient?.prenom,
                            numero: createdCardData?.fidelityInfo?.ficheClient?.numeroCarte
                        });
                    }
                }

            } else {
                notifyError(result.message || "Une erreur est survenue lors de la création de la carte");
                if (onError) {
                    onError();
                }
            }
        } catch (error) {
            console.error("Error creating fidelity card:", error);
            notifyError("Une erreur est survenue lors de la création de la carte");
            if (onError) {
                onError();
            }
        } finally {
            setIsCreating(false);
        }
    };
    // ========== HELPER FUNCTIONS ==========

    const initializeUserData = (companyId, currentStoreId = null, isFavoriteValid = false) => {
        const sourceData = getUserDataWithFidelityPriority(user, companyId);
        
        const initializedFormData = initializeFormData(sourceData);
        
        if (!initializedFormData.store || !isFavoriteValid) {
            initializedFormData.store = currentStoreId || '';
        }
        
        setFormData(initializedFormData);
        setUserName(getUserDisplayName(sourceData));
        setUserMail(sourceData?.email || '');
        setUserGender(sourceData?.gender || '');

        // Calculate progress
        const formattedBirthDate = sourceData?.birthDate?.split('T')[0] || '';
        const progressData = {
            coordonnees: {
                nom: sourceData?.nom,
                prenom: sourceData?.prenom,
                email: sourceData?.email
            },
            genre: sourceData?.gender,
            dateNaissance: formattedBirthDate,
            telephone: sourceData?.phoneNumber || sourceData?.telephone,
            favoriteStore: initializedFormData.store
        };

        const progress = calculateFormProgress(
            progressData,
            isEmailValid(sourceData?.email),
            isValidBirthDate(formattedBirthDate),
            isValidPhoneNumber(progressData.telephone),
            isValidGender(sourceData?.gender)
        );
        setProgressPercentage(progress);
    };

    // ========== API CALLS ==========

    const getCompanyInfo = async () => {
        try {
            setIsCreating(true);
            const result = await axiosInstance.get(`/company/${storeId}`);
            const fetchedCompanyInfo = result.data;
            setCompanyInfo(fetchedCompanyInfo);
            
            // Initialize user data after company info is fetched
            if (fetchedCompanyInfo?.id) {
                initializeUserData(fetchedCompanyInfo.id, storeId, favoriteStoreIsMatch);
            }
            
            return fetchedCompanyInfo;
        } catch (error) {
            console.error("Error fetching company info:", error);
            return null;
        } finally {
            setIsCreating(false);
        }
    };

    // ========== STATE & COMPUTED VALUES ==========

    const isLoading = externalLoading || isCreating;
    const disabled = isLoading || !isActiveStoreValid || missingFieldsCount > 0;
    const [userName, setUserName] = useState('');
    const [userMail, setUserMail] = useState('');

    // ========== STYLE HELPERS ==========
    
    const getButtonStyle = () => {
        const isComplete = progressPercentage === 100;
        
        return {
            style: {
                background: isComplete 
                    ? "linear-gradient(140deg, rgba(225, 255, 38, 1) 15%, rgb(255, 251, 38))" 
                    : "white"
            },
            className: clsx(
                "flex items-center transform transition-all duration-300 justify-between gap-4 w-full rounded-2xl p-4 mb-4",
                isComplete 
                    ? "shadow-[0px_8px_0px_#D9CA0C] active:shadow-[0px_0px_0px_#D9CA0C] active:translate-y-2 pt-5"
                    : "shadow-[0px_6px_0px_0px_rgba(255,255,255,0.26)] active:shadow-[0px_0px_0px_0px_rgba(255,255,255,0.26)] active:translate-y-2 pt-8"
            )
        };
    };

    const buttonStyle = getButtonStyle();

    const badgeStyle = {
        style: {
            background: "linear-gradient(100deg, rgba(225, 255, 38, 1) 0%, rgb(255, 233, 38) 100%)",
        },
        className: "geomanistRegular shadow-[0px_0px_0px_3px_rgba(255,255,255,0.4)] text-black font-bold text-2xl p-4 rounded-xl -mb-4 ml-4 z-10"
    };

    useEffect(() => {
        if (user?._id && storeId) {
            getCompanyInfo();
        }
    }, [user?._id, storeId]);
    return (
        <div className="flex flex-col items-center justify-center fixed bottom-0 left-1/2 transform -translate-x-1/2 p-4 w-[100dvw] z-50 gap-4 py-8 backdrop-blur-[2px] bg-gradient-to-t from-black/40 to-transparent">
            {/* User Profile Button */}
            <div className="flex flex-col items-start w-full">
                {/* Progress badge - Only show if not complete */}
                {progressPercentage < 100 && (
                    <div
                        style={badgeStyle.style}
                        className={badgeStyle.className}
                    >
                        {isLoading ? 'Chargement...' : `${progressPercentage}% déjà rempli`}
                    </div>
                )}
                
                <button
                    type="button"
                    style={buttonStyle.style}
                    className={buttonStyle.className}
                    onClick={progressPercentage === 100 ? handleCreateCard : handleGoToUpdateUser}
                    disabled={isCreating}
                >
                    <div className="flex items-center justify-center gap-4">
                        <img src={miniLogo} className="w-12 h-12" alt="mini-logo" />
                        <span className="text-3xl geomanistBold text-[#000]">
                            {isCreating ? 'Chargement...' : 'Créer ma carte'}
                        </span>
                    </div>
                    <FaChevronRight size={20} className="fill-[#727272]" />
                </button>
            </div>

            {/* Opt-ins - Only visible when profile is complete */}
            <div
                className={clsx(
                    "w-full transition-all duration-300 overflow-hidden",
                    missingFieldsCount === 0 ? "max-h-[200px]" : "max-h-[0px]"
                )}
            >
                <OptinStep
                    emailOptin={emailOptin}
                    smsOptin={smsOptin}
                    onEmailOptinChange={(checked) => setEmailOptin(checked)}
                    onSmsOptinChange={(checked) => setSmsOptin(checked)}
                    showEmailOptin={true}
                    showSmsOptin={true}
                    className="w-full"
                />
            </div>

            {/* Link Existing Card Button */}
            <button
                type="button"
                className="flex flex-col gap-2 geomanistMedium mt-4 text-2xl underline text-white"
                onClick={goToAddCard}
            >
                {isLoading ? "Chargement..." : "Lier une carte existante"}
            </button>
        </div>
    )
}
